import React, { useCallback, useEffect } from "react";
import styles from "./OrderSetting.module.scss";
import { Skeleton, Switch } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import { getOrderSettings, setOrderSettings } from "@/store/slices/orderSlice";
import { useAppDispatch } from "@/store/hooks";
import toast from "@/lib/toast";
import { ButtonLoader } from "@/components/Loader/ButtonLoader";

type FormValues = {
  AutoMarkVisitAsProductive: boolean;
  OrderEditDurationInDays: number;
};

const OrderSettingSkeleton: React.FC = () => {
  return (
    <div className={styles.orderSettingsWrapper}>
      <div className={styles.orderSettingContainer}>
        <div className={styles.orderSettings}>
          <div className={styles.orderSetting}>
            <Skeleton variant="text" width={200} height={24} />
            <Skeleton
              variant="rounded"
              width={38}
              height={22}
              sx={{ borderRadius: "12px" }}
            />
          </div>

          <div className={styles.orderSetting}>
            <Skeleton variant="text" width={180} height={24} />
            <Skeleton
              variant="rounded"
              width={90}
              height={36}
              sx={{ borderRadius: "8px" }}
            />
          </div>

          <div className={styles.orderSettingFooter}>
            <Skeleton
              variant="rounded"
              width={82}
              height={36}
              sx={{ borderRadius: "7px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderSetting: React.FC = () => {
  const dispatch = useAppDispatch();

  const { orderSettings, loading, error } = useSelector(
    (state: RootState) => state.orders
  );

  const { control, reset, getValues } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      AutoMarkVisitAsProductive: false,
      OrderEditDurationInDays: 0,
    },
  });

  const fetchOrderSettings = useCallback(() => {
    dispatch(getOrderSettings());
  }, [dispatch]);

  useEffect(() => {
    fetchOrderSettings();
  }, [fetchOrderSettings]);

  useEffect(() => {
    if (orderSettings) {
      reset({
        AutoMarkVisitAsProductive: Boolean(orderSettings.AutoMarkVisitAsProductive),
        OrderEditDurationInDays: Number(orderSettings.OrderEditDurationInDays ?? 0),
      });
    }
  }, [orderSettings, reset]);

  const handleSave = () => {
    const values = getValues();
    const payload = {
      AutoMarkVisitAsProductive: Boolean(values.AutoMarkVisitAsProductive),
      OrderEditDurationInDays: Number(values.OrderEditDurationInDays || 0),
    };

    dispatch(setOrderSettings(payload))
      .unwrap()
      .then(() => {
        toast.success("Order Settings Saved Successfully");
        reset(payload);
      })
      .catch((err) => toast.error(err || "Failed to save order settings"));
  };

  if (loading?.getOrderSetting || (!orderSettings && !error)) {
    return <OrderSettingSkeleton />;
  }

  return (
    <div className={styles.orderSettingsWrapper}>
      <div className={styles.orderSettingContainer}>
        <div className={styles.orderSettings}>
          <div className={styles.orderSetting}>
            <span className={styles.label}>
              Auto Mark Visit As Productive
            </span>
            <Controller
              name="AutoMarkVisitAsProductive"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  onChange={(_, checked) => field.onChange(checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#5d78ff",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#5d78ff",
                    },
                  }}
                />
              )}
            />
          </div>

          <div className={styles.orderSetting}>
            <span className={styles.label}>
              Order Edit Duration In Days
            </span>
            <Controller
              name="OrderEditDurationInDays"
              control={control}
              render={({ field }) => (
                <input
                  type="number"
                  min={0}
                  className={styles.inputNumber}
                  value={field.value ?? 0}
                  onChange={(e) => {
                    const val = e.target.value === "" ? 0 : Number(e.target.value);
                    field.onChange(val);
                  }}
                />
              )}
            />
          </div>

          <div className={styles.orderSettingFooter}>
            <ButtonLoader
              loading={loading.OrderSetting}
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: "#5d78ff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "7px",
                padding: "6px 24px",
                boxShadow: "0 3px 10px rgba(93, 120, 255, 0.24)",
                "&:hover": {
                  backgroundColor: "#4863e6",
                  boxShadow: "0 5px 14px rgba(93, 120, 255, 0.32)",
                },
              }}
            >
              Save
            </ButtonLoader>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSetting;